// Déjà Vu Nomad - Professional Business Color Constants
// Inspired by enterprise SaaS and business travel tools

import { createTheme } from '@mui/material/styles';

// Theme constants mapping to new professional color scheme
// This provides backward compatibility while we use the new color palette

export const THEME_COLORS = {
  primary: {
    main: '#591716',    // Accent Burgundy
    light: '#7a1f1e',   // Lighter burgundy variant
    dark: '#3d0f0e',    // Darker burgundy variant
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#313848',    // Secondary Dark
    light: '#465066',   // Lighter secondary variant
    dark: '#1c202a',    // Darker secondary variant
    contrastText: '#FFFFFF',
  },
  accent: {
    main: '#591716',    // Same as primary for consistency
    light: '#7a1f1e',
    dark: '#3d0f0e',
    contrastText: '#FFFFFF',
  },
  categories: {
    hotel: {
      main: '#313848',    // Using secondary dark for hotels
      light: '#465066',   // Lighter secondary variant
      dark: '#1c202a',    // Darker secondary variant
      gradient: 'linear-gradient(90deg, #313848 0%, #1c202a 100%)',
    },
    restaurant: {
      main: '#EA580C',
      light: '#FB923C',
      dark: '#C2410C',
      gradient: 'linear-gradient(90deg, #EA580C 0%, #C2410C 100%)',
    },
    event: {
      main: '#591716',    // Using burgundy for events
      light: '#7a1f1e',   // Lighter burgundy variant
      dark: '#3d0f0e',    // Darker burgundy variant
      gradient: 'linear-gradient(90deg, #591716 0%, #3d0f0e 100%)',
    },
    transport: {
      main: '#059669',
      light: '#10B981',
      dark: '#047857',
      gradient: 'linear-gradient(90deg, #059669 0%, #047857 100%)',
    },
    activity: {
      main: '#DC2626',
      light: '#EF4444',
      dark: '#B91C1C',
      gradient: 'linear-gradient(90deg, #DC2626 0%, #B91C1C 100%)',
    },
    other: {
      main: '#475569',
      light: '#64748B',
      dark: '#334155',
      gradient: 'linear-gradient(90deg, #475569 0%, #334155 100%)',
    },
  },
  status: {
    success: '#059669',
    warning: '#D97706',
    error: '#DC2626',
    info: '#0EA5E9',
  },
  background: {
    default: '#ffffff', // Pure White
    paper: '#FFFFFF',
    light: '#f8f9fa',   // Extended light variant
    grey: '#E2E8F0',
    glassMorphism: 'rgba(255, 255, 255, 0.1)',
    white: '#ffffff',   // Pure White
  },
  text: {
    primary: '#111111',   // Primary Black
    secondary: '#313848', // Secondary Dark
    light: '#64748B',
    muted: '#465066',     // Extended muted variant
    white: '#ffffff',     // Pure White
    disabled: '#999999',  // Disabled state color
  },
  border: {
    light: '#E2E8F0',
    medium: '#CBD5E1',
    dark: '#94A3B8',
  },
};

// Enhanced gradients for different UI elements
export const THEME_GRADIENTS = {
  primary: `linear-gradient(135deg, ${THEME_COLORS.primary.main} 0%, ${THEME_COLORS.primary.light} 100%)`,
  primaryReverse: `linear-gradient(135deg, ${THEME_COLORS.primary.light} 0%, ${THEME_COLORS.primary.main} 100%)`,
  secondary: `linear-gradient(135deg, ${THEME_COLORS.secondary.main} 0%, ${THEME_COLORS.secondary.light} 100%)`,
  accent: 'linear-gradient(90deg, #8e9aaf 0%, #64748b 100%)',
  
  // Status gradients for alerts and notifications
  success: 'linear-gradient(90deg, #059669 0%, #047857 100%)',
  error: 'linear-gradient(90deg, #DC2626 0%, #B91C1C 100%)',
  warning: 'linear-gradient(90deg, #D97706 0%, #B45309 100%)',
  info: 'linear-gradient(90deg, #0EA5E9 0%, #1A365D 100%)',
  
  // Specialty gradients
  glass: 'linear-gradient(90deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.1) 100%)',
  overlay: `linear-gradient(135deg, ${THEME_COLORS.primary.main}15 0%, ${THEME_COLORS.secondary.main}15 100%)`,
  overlayReverse: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.6) 100%)',
  
  // Navigation gradient
  navigation: `linear-gradient(135deg, ${THEME_COLORS.primary.main}95 0%, ${THEME_COLORS.secondary.main}95 100%)`,
  navigationAlt: `linear-gradient(135deg, ${THEME_COLORS.secondary.main}95 0%, ${THEME_COLORS.primary.main}95 100%)`,
};

export const THEME_SHADOWS = {
  none: 'none',
  light: '0px 2px 8px rgba(17, 17, 17, 0.08)',
  medium: '0px 4px 16px rgba(17, 17, 17, 0.12)',
  heavy: '0px 8px 24px rgba(17, 17, 17, 0.15)',
  colored: '0 4px 6px -1px rgba(26, 54, 93, 0.1), 0 2px 4px -1px rgba(26, 54, 93, 0.06)',
  hover: '0 10px 15px -3px rgba(26, 54, 93, 0.1), 0 4px 6px -2px rgba(26, 54, 93, 0.05)',
  glass: '0 4px 6px -1px rgba(31, 38, 135, 0.1), 0 2px 4px -1px rgba(31, 38, 135, 0.06)',
  card: '0px 2px 8px rgba(17, 17, 17, 0.08)',
  cardHover: '0px 8px 24px rgba(17, 17, 17, 0.12)',
};

export const THEME_BORDERS = {
  none: 'none',
  light: '1px solid rgba(0, 0, 0, 0.05)',
  medium: '1px solid rgba(0, 0, 0, 0.1)',
  primary: '1px solid rgba(26, 54, 93, 0.2)',
  secondary: '1px solid rgba(74, 85, 104, 0.2)',
  white: '1px solid rgba(255, 255, 255, 0.2)',
  whiteLight: '1px solid rgba(255, 255, 255, 0.1)',
  dashed: '2px dashed #E2E8F0',
};

export const THEME_RADIUS = {
  none: 0,
  small: 2,
  medium: 3,
  large: 4,
  xlarge: 6, // Maximum rounding for special cases
  round: '50%', // Only for circular elements like avatars
  card: 3,
};

export const THEME_SPACING = {
  xs: 4,   // 0.5 * 8
  sm: 8,   // 1 * 8
  md: 16,  // 2 * 8
  lg: 24,  // 3 * 8
  xl: 32,  // 4 * 8
  xxl: 48, // 6 * 8
  xxxl: 64, // 8 * 8
};

export const THEME_TRANSITIONS = {
  default: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  smooth: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  fast: 'all 0.2s ease',
  bounce: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  slow: 'all 0.5s ease',
};

// Enhanced breakpoints for consistent responsive design
export const THEME_BREAKPOINTS = {
  xs: '@media (max-width: 599px)',
  sm: '@media (min-width: 600px)',
  md: '@media (min-width: 960px)',
  lg: '@media (min-width: 1280px)',
  xl: '@media (min-width: 1920px)',
};

// Helper functions for easy use
export const getCategoryColor = (category) => {
  return THEME_COLORS.categories[category]?.main || THEME_COLORS.categories.other.main;
};

export const getCategoryLight = (category) => {
  return THEME_COLORS.categories[category]?.light || THEME_COLORS.categories.other.light;
};

export const getCategoryDark = (category) => {
  return THEME_COLORS.categories[category]?.dark || THEME_COLORS.categories.other.dark;
};

export const getCategoryGradient = (category) => {
  return THEME_COLORS.categories[category]?.gradient || THEME_COLORS.categories.other.gradient;
};

// Common style objects for consistent use across components
export const COMMON_STYLES = {
  // Glass morphism effect
  glassMorphism: {
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(15px)',
    border: THEME_BORDERS.white,
    boxShadow: THEME_SHADOWS.glass,
  },
  
  // Enhanced glass morphism for navigation
  glassMorphismNav: {
    background: 'rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(20px)',
    border: THEME_BORDERS.whiteLight,
  },
  
  // Professional card style
  professionalCard: {
    borderRadius: THEME_RADIUS.small,
    boxShadow: THEME_SHADOWS.light,
    border: THEME_BORDERS.light,
    background: THEME_COLORS.background.paper,
    transition: THEME_TRANSITIONS.smooth,
    '&:hover': {
      boxShadow: THEME_SHADOWS.medium,
      transform: 'translateY(-4px)',
      borderColor: THEME_COLORS.primary.main,
    },
  },
  
  // Professional button style
  professionalButton: {
    borderRadius: THEME_RADIUS.small,
    fontWeight: 700,
    textTransform: 'none',
    transition: THEME_TRANSITIONS.default,
    background: THEME_COLORS.primary.main,
    color: THEME_COLORS.text.white,
    padding: `${THEME_SPACING.sm}px ${THEME_SPACING.lg}px`,
    '&:hover': {
      background: THEME_COLORS.primary.dark,
      transform: 'translateY(-2px)',
      boxShadow: THEME_SHADOWS.hover,
    },
    '&:disabled': {
      background: THEME_COLORS.secondary.light,
      color: THEME_COLORS.text.muted,
      transform: 'none',
      boxShadow: 'none',
    },
  },
  
  // Secondary button style
  secondaryButton: {
    borderRadius: THEME_RADIUS.small,
    fontWeight: 600,
    textTransform: 'none',
    transition: THEME_TRANSITIONS.default,
    background: 'transparent',
    color: THEME_COLORS.primary.main,
    border: `2px solid ${THEME_COLORS.primary.main}`,
    padding: `${THEME_SPACING.sm}px ${THEME_SPACING.lg}px`,
    '&:hover': {
      background: THEME_COLORS.primary.main,
      color: THEME_COLORS.text.white,
      transform: 'translateY(-2px)',
      boxShadow: THEME_SHADOWS.medium,
    },
  },
  
  // Ghost button style
  ghostButton: {
    borderRadius: THEME_RADIUS.small,
    fontWeight: 600,
    textTransform: 'none',
    transition: THEME_TRANSITIONS.default,
    background: 'transparent',
    color: THEME_COLORS.primary.main,
    border: 'none',
    padding: `${THEME_SPACING.sm}px ${THEME_SPACING.md}px`,
    '&:hover': {
      background: `${THEME_COLORS.primary.main}15`,
      transform: 'translateY(-1px)',
    },
  },
  
  // Professional input style
  professionalInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: THEME_RADIUS.small,
      transition: THEME_TRANSITIONS.default,
      backgroundColor: THEME_COLORS.background.paper,
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: THEME_COLORS.primary.main,
        borderWidth: 2,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: THEME_COLORS.primary.main,
        borderWidth: 2,
        boxShadow: `0 0 0 3px ${THEME_COLORS.primary.light}20`,
      },
    },
    '& .MuiInputLabel-root': {
      fontWeight: 500,
      '&.Mui-focused': {
        color: THEME_COLORS.primary.main,
      },
    },
  },
  
  // Professional dialog style
  professionalDialog: {
    '& .MuiDialog-paper': {
      borderRadius: THEME_RADIUS.small,
      boxShadow: THEME_SHADOWS.heavy,
      border: THEME_BORDERS.light,
      background: THEME_COLORS.background.paper,
    },
  },
  
  // Navigation link style
  navLink: {
    color: THEME_COLORS.text.white,
    fontWeight: 600,
    textDecoration: 'none',
    padding: `${THEME_SPACING.sm}px ${THEME_SPACING.lg}px`,
    borderRadius: THEME_RADIUS.small,
    transition: THEME_TRANSITIONS.default,
    border: '1px solid transparent',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.15)',
      backdropFilter: 'blur(20px)',
      border: THEME_BORDERS.whiteLight,
      transform: 'translateY(-1px)',
    },
    '&.active': {
      background: 'rgba(255, 255, 255, 0.15)',
      backdropFilter: 'blur(20px)',
      border: THEME_BORDERS.whiteLight,
    },
  },
  
  // Hero section style
  heroSection: {
    background: THEME_GRADIENTS.navigation,
    color: THEME_COLORS.text.white,
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.08"%3E%3Ccircle cx="30" cy="30" r="1.5"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
      opacity: 0.6,
    },
  },
  
  // Page container style
  pageContainer: {
    minHeight: '100vh',
    backgroundColor: THEME_COLORS.background.default,
    paddingTop: THEME_SPACING.xl,
    paddingBottom: THEME_SPACING.xl,
  },
  
  // Content wrapper style
  contentWrapper: {
    backgroundColor: THEME_COLORS.background.paper,
    borderRadius: THEME_RADIUS.small,
    boxShadow: THEME_SHADOWS.light,
    border: THEME_BORDERS.light,
    padding: THEME_SPACING.xl,
    margin: '0 auto',
  },
};

// Category icon mappings for consistency
export const CATEGORY_ICONS = {
  hotel: 'Hotel',
  restaurant: 'Restaurant', 
  event: 'Event',
  transport: 'DirectionsCar',
  activity: 'LocalActivity',
  other: 'Place',
};

const themeConstants = {
  THEME_COLORS,
  THEME_GRADIENTS,
  THEME_SHADOWS,
  THEME_BORDERS,
  THEME_RADIUS,
  THEME_SPACING,
  THEME_TRANSITIONS,
  THEME_BREAKPOINTS,
  getCategoryColor,
  getCategoryLight,
  getCategoryDark,
  getCategoryGradient,
  COMMON_STYLES,
  CATEGORY_ICONS,
};

// Utility functions for ensuring proper contrast
export const getContrastSafeTextColor = (backgroundColor) => {
  // For any primary/secondary/dark colors, return white text
  const darkColors = [
    THEME_COLORS.primary.main,
    THEME_COLORS.primary.dark,
    THEME_COLORS.secondary.main,
    THEME_COLORS.secondary.dark,
    THEME_COLORS.accent.main,
    THEME_COLORS.accent.dark,
  ];
  
  if (darkColors.includes(backgroundColor) || backgroundColor.includes('gradient')) {
    return THEME_COLORS.text.white;
  }
  
  return THEME_COLORS.text.primary;
};

// Helper for creating color-safe components with proper contrast
export const createContrastSafeStyle = (backgroundColor, overrides = {}) => {
  return {
    backgroundColor,
    color: getContrastSafeTextColor(backgroundColor),
    ...overrides,
  };
};

// Predefined safe color combinations
export const SAFE_COLOR_COMBINATIONS = {
  primaryButton: {
    backgroundColor: THEME_COLORS.primary.main,
    color: THEME_COLORS.text.white,
    '&:hover': {
      backgroundColor: THEME_COLORS.primary.dark,
      color: THEME_COLORS.text.white,
    },
  },
  secondaryButton: {
    backgroundColor: THEME_COLORS.secondary.main,
    color: THEME_COLORS.text.white,
    '&:hover': {
      backgroundColor: THEME_COLORS.secondary.dark,
      color: THEME_COLORS.text.white,
    },
  },
  primaryChip: {
    backgroundColor: THEME_COLORS.primary.main,
    color: THEME_COLORS.text.white,
  },
  primaryAvatar: {
    backgroundColor: THEME_COLORS.primary.main,
    color: THEME_COLORS.text.white,
  },
  gradientHero: {
    background: THEME_GRADIENTS.primary,
    color: THEME_COLORS.text.white,
  },
};

export default themeConstants;

// Create a proper Material-UI theme object
const theme = createTheme({
  palette: {
    primary: {
      main: THEME_COLORS.primary.main,
      light: THEME_COLORS.primary.light,
      dark: THEME_COLORS.primary.dark,
      contrastText: THEME_COLORS.primary.contrastText,
    },
    secondary: {
      main: THEME_COLORS.secondary.main,
      light: THEME_COLORS.secondary.light,
      dark: THEME_COLORS.secondary.dark,
      contrastText: THEME_COLORS.secondary.contrastText,
    },
    background: {
      default: THEME_COLORS.background.default,
      paper: THEME_COLORS.background.paper,
    },
    text: {
      primary: THEME_COLORS.text.primary,
      secondary: THEME_COLORS.text.secondary,
    },
    error: {
      main: THEME_COLORS.status.error,
    },
    warning: {
      main: THEME_COLORS.status.warning,
    },
    info: {
      main: THEME_COLORS.status.info,
    },
    success: {
      main: THEME_COLORS.status.success,
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.43,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 12,
  },
  spacing: (factor) => `${THEME_SPACING.sm * factor}px`,
  shadows: [
    'none',
    THEME_SHADOWS.light,
    THEME_SHADOWS.medium,
    THEME_SHADOWS.heavy,
    ...Array(21).fill('none'), // Material-UI expects 25 shadows
  ],
});

export { theme }; 